import { RendererStyleFlags2 } from '@angular/core';
import { assert } from 'src/app/core/assert/assert';
import { ShiveRenderService } from '../../../../../core/services/rendering/shive-render.service';
import { CalendarService } from '../../../services/calendar.service';
import {
  CSSHasNextWeekPart,
  CSSHasPrevWeekPart,
  DayOrWeekVariableIntervalTimeBlockGapPx,
  FixIntervalContainerTopSpacing,
  HorizontalTimeBlockGapPx,
} from '../../../util/calendar-style.constants';
import { DateTimeHelper } from '../../../util/date-time-helper';
import { ITimeBlockComponentItem } from '../types/time-block-component-item-types';
import { IFixIntervalViewType } from '../types/time-block-types';
import { CssAttributes } from './css-attributes';
import { TimeBlockElementSelectorService } from './time-block-element-selector.service';

export class TimeBlockRenderer {
  constructor(
    private readonly shiveRenderService: ShiveRenderService,
    private readonly calendarService: CalendarService,
  ) {}

  public renderDayOrWeekViewVariableIntervalTimeBlock(timeBlock: ITimeBlockComponentItem): void {
    const geometryData = timeBlock.timeBlockModel.timeBlockViewType.geometryData;
    const timeBlockHTMLWrapper = TimeBlockElementSelectorService.getTimeBlockHTMLWrapper(timeBlock);

    assert(geometryData.height > 0, 'Time block height is invalid.');

    this.shiveRenderService.setStyle(
      timeBlockHTMLWrapper,
      CssAttributes.height,
      `${geometryData.height}px`,
    );
    this.shiveRenderService.setStyle(
      timeBlockHTMLWrapper,
      CssAttributes.top,
      `${geometryData.top}px`,
    );

    const left = geometryData.containerLeftEdgeToTimeBlockLeftEdge;
    this.shiveRenderService.setStyle(timeBlockHTMLWrapper, CssAttributes.left, `${left}%`);
    const width = geometryData.width;
    this.shiveRenderService.setStyle(
      timeBlockHTMLWrapper,
      CssAttributes.width,
      `calc(${width}% - ${DayOrWeekVariableIntervalTimeBlockGapPx}px)`,
    );
  }

  public renderDayOrWeekViewFixIntervalTimeBlock(
    timeBlock: ITimeBlockComponentItem,
    fixIntervalContainerWidth: number,
  ): void {
    if (!fixIntervalContainerWidth || fixIntervalContainerWidth <= 0) {
      throw new Error(`Full day container width is invalid: ${fixIntervalContainerWidth}`);
    }

    const timeBlockViewType = timeBlock.timeBlockModel.timeBlockViewType as IFixIntervalViewType;
    const width = (timeBlockViewType.geometryData.width / fixIntervalContainerWidth) * 100;
    const left = (timeBlockViewType.geometryData.left / fixIntervalContainerWidth) * 100;
    let top = HorizontalTimeBlockGapPx + FixIntervalContainerTopSpacing;
    if (timeBlockViewType.parallelIndex >= 0) {
      const parallelIndex = timeBlockViewType.parallelIndex;
      const height = timeBlockViewType.geometryData.height;
      timeBlockViewType.geometryData.top = top =
        height * parallelIndex +
        parallelIndex * 2 +
        HorizontalTimeBlockGapPx +
        FixIntervalContainerTopSpacing;
    }

    const timeBlockHTMLWrapper = TimeBlockElementSelectorService.getTimeBlockHTMLWrapper(timeBlock);
    const calendarProperties = this.calendarService.model.calendarProperties;
    if (
      DateTimeHelper.isBefore(timeBlock.timeBlockModel.start, calendarProperties.visibleStartDate)
    ) {
      this.shiveRenderService.addClass(
        timeBlock.timeBlockModel.componentRef.instance.timeBlockHTMLWrapper.nativeElement,
        CSSHasPrevWeekPart,
      );
    }

    if (DateTimeHelper.isAfter(timeBlock.timeBlockModel.end, calendarProperties.visibleEndDate)) {
      this.shiveRenderService.addClass(
        timeBlock.timeBlockModel.componentRef.instance.timeBlockHTMLWrapper.nativeElement,
        CSSHasNextWeekPart,
      );
    }

    this.shiveRenderService.setStyle(
      timeBlockHTMLWrapper,
      CssAttributes.left,
      `${left}%`,
      RendererStyleFlags2.Important,
    );
    this.shiveRenderService.setStyle(
      timeBlockHTMLWrapper,
      CssAttributes.width,
      `calc(${Math.abs(width)}%)`,
    );
    this.shiveRenderService.setStyle(
      timeBlockHTMLWrapper,
      CssAttributes.transform,
      `translate(0, ${top}px)`,
    );
  }

  public renderMonthViewFixIntervalTimeBlock(timeBlock: ITimeBlockComponentItem): void {
    const timeBlockHTMLWrapper = TimeBlockElementSelectorService.getTimeBlockHTMLWrapper(timeBlock);
    const width = timeBlock.timeBlockModel.timeBlockViewType.geometryData.width;

    const timeBlockViewType = timeBlock.timeBlockModel.timeBlockViewType as IFixIntervalViewType;
    let top = 0;
    const parallelIndex = timeBlockViewType.parallelIndex;
    if (parallelIndex > 0) {
      const height = timeBlockViewType.geometryData.height;
      timeBlockViewType.geometryData.top = top =
        height * parallelIndex + parallelIndex * HorizontalTimeBlockGapPx;
    }

    this.shiveRenderService.setStyle(
      timeBlockHTMLWrapper,
      CssAttributes.transform,
      `translate(0, ${top}px)`,
    );

    if (timeBlock.timeBlockModel.componentRef !== null) {
      const calendarModel = this.calendarService.model;
      // Check if week has a previous week, i.e. if the partNumber is 0 or if the full day time block starts before the visible start of the
      // current month
      const visibleStartDate = calendarModel.calendarProperties.visibleStartDate;
      if (
        timeBlock.timeBlockModel.partNumber > 0 ||
        DateTimeHelper.isBefore(
          timeBlock.timeBlockModel.start,
          calendarModel.calendarProperties.visibleStartDate,
        )
      ) {
        this.shiveRenderService.addClass(
          timeBlock.timeBlockModel.componentRef.instance.timeBlockHTMLWrapper.nativeElement,
          CSSHasPrevWeekPart,
        );
      }

      // Calculate the day difference between the visible calendar start and the time block start. Need to be done since partNumber is 0
      // for a time block that probably starts before the current month.
      let offsetDays = DateTimeHelper.differenceInDays(
        visibleStartDate,
        timeBlock.timeBlockModel.start,
      );
      offsetDays = offsetDays < 0 ? 0 : offsetDays;

      // Calculate the correct current date.
      const curDate = DateTimeHelper.addDays(
        timeBlock.timeBlockModel.start,
        timeBlock.timeBlockModel.partNumber + offsetDays,
      );
      const endOfWeek = DateTimeHelper.endOfWeek(curDate, {
        weekStartsOn: this.calendarService.model.calendarProperties.weekStartsOn,
      });
      // Check if week has a subsequent week
      if (DateTimeHelper.isAfter(timeBlock.timeBlockModel.end, endOfWeek)) {
        this.shiveRenderService.addClass(
          timeBlock.timeBlockModel.componentRef.instance.timeBlockHTMLWrapper.nativeElement,
          CSSHasNextWeekPart,
        );
      }
    }

    this.shiveRenderService.setStyle(timeBlockHTMLWrapper, CssAttributes.width, `calc(${width}px`);
  }

  public renderMonthViewVariableIntervalTimeBlock(timeBlock: ITimeBlockComponentItem): void {
    let top = 0;
    const timeBlockHTMLWrapper = TimeBlockElementSelectorService.getTimeBlockHTMLWrapper(timeBlock);
    const timeBlockViewType = timeBlock.timeBlockModel.timeBlockViewType as IFixIntervalViewType;
    const parallelIndex = timeBlockViewType.parallelIndex;
    if (!parallelIndex || parallelIndex < 0) {
      return;
    }

    const height = timeBlockViewType.geometryData.height;
    timeBlockViewType.geometryData.top = top =
      height * parallelIndex + parallelIndex * HorizontalTimeBlockGapPx;
    this.shiveRenderService.setStyle(timeBlockHTMLWrapper, CssAttributes.top, `calc(${top}px)`);
  }
}
